import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { DatasetStringValueMapperDTO } from "../../../charts/interfaces";
import { PermissionGuard } from "components/wrappers/PermissionGuard";

interface CreateEditDatasetMapperProps {
  newId?: string;
  mapperData?: DatasetStringValueMapperDTO;
  onSave: (data: DatasetStringValueMapperDTO) => void;
  onCancel: () => void;
}
const CreateEditDatasetMapper = ({
  newId,
  mapperData,
  onSave,
  onCancel,
}: CreateEditDatasetMapperProps) => {
  const [datasetMapperTitle, setDatasetMapperTitle] = useState<string>("");
  const [global, setGlobal] = useState<boolean>(false);
  const [mappings, setMappings] = useState<{ key: string; value: string }[]>([
    { key: "", value: "" },
  ]);
  const [newMapper, setNewMapper] = useState<DatasetStringValueMapperDTO>();

  useEffect(() => {
    if (newId) {
      createDatasetMapperHandler();
    }
  }, [global, mappings, datasetMapperTitle]);

  useEffect(() => {
    if (!newId && mapperData) {
      setDatasetMapperTitle(mapperData.title);
      setGlobal(mapperData.global);
      setMappings(
        Object.entries(mapperData.mappings).map(([key, value]) => ({
          key,
          value,
        }))
      );
    }
  }, [mapperData]);

  const createDatasetMapperHandler = () => {
    if (newId) {
      const data: DatasetStringValueMapperDTO = {
        id: newId,
        global: global,
        title: datasetMapperTitle,
        mappings: mappings.reduce((acc: any, curr) => {
          if (curr.key && curr.value) {
            acc[curr.key] = curr.value;
          }
          return acc;
        }, {}),
      };
      setNewMapper(data);
    }
  };

  const handleMappingChange = (index: number, key: string, value: string) => {
    const newMappings = [...mappings];
    newMappings[index] = { key, value };
    setMappings(newMappings);
  };

  const addMapping = () => {
    setMappings([...mappings, { key: "", value: "" }]);
  };

  const removeMapping = (index: number) => {
    const newMappings = [...mappings];
    newMappings.splice(index, 1);
    setMappings(newMappings);
  };

  const handleSave = () => {
    if (newId) {
      if (newMapper?.title.trim() === "") {
        easasAlert("Title missing", "Please enter a title", "error");
        return;
      }
      if (Object.keys(newMapper?.mappings ?? {}).length === 0) {
        easasAlert(
          "Mappings missing",
          "Please enter at least one mapping",
          "error"
        );
        return;
      }
      if (newMapper) {
        onSave(newMapper);
      }
    } else {
      if (datasetMapperTitle.trim() === "") {
        easasAlert("Title missing", "Please enter a title", "error");
        return;
      }
      if (mapperData) {
        const data: DatasetStringValueMapperDTO = {
          id: mapperData.id,
          global,
          company: mapperData.company,
          title: datasetMapperTitle,
          mappings: mappings.reduce((acc: any, { key, value }) => {
            if (key && value) {
              acc[key] = value;
            }
            return acc;
          }, {}),
        };
        if (Object.keys(data?.mappings ?? {}).length === 0) {
          easasAlert(
            "Mappings missing",
            "Please enter at least one mapping",
            "error"
          );
          return;
        }
        onSave(data);
      }
    }
  };

  return (
    <div className="grid">
      <div>
        <div className="flex col-12 align-items-center">
          <p className="col-4 mb-0">Dataset mapper title</p>
          <InputText
            className="w-full"
            value={datasetMapperTitle}
            onChange={(e) => setDatasetMapperTitle(e.target.value)}
            placeholder="New Dataset Mapper Title"
          />
        </div>
        <PermissionGuard permissions="ADMIN">
          <div className="flex col-12 align-items-center">
            <p className="col-4 mb-0">Global</p>
            <InputSwitch
              checked={global}
              onChange={(e) => setGlobal(e.value)}
            />
          </div>
        </PermissionGuard>
        <h6>Mappings</h6>
        {mappings.map((mapping, index) => (
          <div key={index} className="flex col-12 align-items-center gap-2">
            <InputText
              className="w-full"
              aria-placeholder="Index"
              value={mapping.key}
              onChange={(e) =>
                handleMappingChange(index, e.target.value, mapping.value)
              }
              placeholder="Key"
              keyfilter="int"
            />
            <InputText
              className="w-full"
              aria-placeholder="Meaning"
              value={mapping.value}
              onChange={(e) =>
                handleMappingChange(index, mapping.key, e.target.value)
              }
              placeholder="Value"
            />
            <Button
              type="button"
              icon="pi pi-trash"
              className="const-del-btn  p-button-rounded p-button-text p-button-danger"
              onClick={() => removeMapping(index)}
            />
          </div>
        ))}
        <div className="col-12">
          <Button
            className="p-button-outlined p-button-secondary"
            label="Add Mapping"
            onClick={addMapping}
          />
        </div>
        <div className="flex col-12 gap-2">
          <Button
            className="p-button-success"
            label={newId ? "Create" : "Save"}
            onClick={handleSave}
          />
          <Button
            className="p-button-danger"
            label="Cancel"
            onClick={onCancel}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateEditDatasetMapper;
